import { api } from "@/modules/api";
import { windowModule } from "@/modules/window";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import type { AppStore } from "@/store/AppStore";
import { components } from "@/__openapi__/backend/schema";
import type { Location } from "react-router-dom";
import { BaseError } from "@/domains/errors";
import { buildStripeReturnUrl, buildStripeSuccessUrl, buildStripeCancelUrl } from "./query-params";
import { isAppleProvider } from "./provider";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { getRewardfulReferral } from "@/modules/rewardful";

interface CreateCheckoutSessionParams {
  billingStrategy: BillingStrategyEnum;
  location: Location;
  store: AppStore;
}

type CheckoutSessionResponse = components["schemas"]["CreateStripeCheckoutSessionResponseSchema"];
type CreateCheckoutSessionRequest =
  components["schemas"]["CreateStripeCheckoutSessionRequestSchema"];

/**
 * Creates a Stripe checkout session and handles navigation based on the response.
 * Handles three scenarios:
 * 1. User needs to go through Stripe checkout (stripe_checkout_session_url)
 * 2. User already has a subscription and needs billing portal (stripe_billing_portal_url)
 * 3. User has an Apple subscription (already_paid + Apple provider)
 */
export async function createCheckoutSessionAndNavigate({
  billingStrategy,
  location,
  store,
}: CreateCheckoutSessionParams): Promise<CheckoutSessionResponse> {
  const successUrl = buildStripeSuccessUrl({ location });
  const cancelUrl = buildStripeCancelUrl({ location });
  const returnUrl = buildStripeReturnUrl({ location }); // used if the checkout gets "upgraded" to a billing portal url by the backend

  const body: CreateCheckoutSessionRequest = {
    cancel_url: cancelUrl,
    success_url: successUrl,
    return_url: returnUrl,
    billing_strategy: billingStrategy,
  };

  const rewardfulReferral = getRewardfulReferral();
  if (rewardfulReferral) {
    body.client_reference_id = rewardfulReferral;
  }

  const response = await api.post("/v2/me/subscriptions/stripe/checkout-session", {
    body,
  });

  if (!response.data) {
    logger.error({
      message: "Failed to create checkout session",
      info: { response: objectModule.safeAsJson(response) },
    });
    throw new BaseError({
      message: "Failed to create checkout session",
      info: { response: objectModule.safeAsJson(response) },
    });
  }

  const { stripe_checkout_session_url, stripe_billing_portal_url, already_paid, provider } =
    response.data;

  // If the backend gave us a billing portal url, use that. It means the user is already subscribed.
  if (stripe_billing_portal_url) {
    windowModule.navigateToExternal({ url: new URL(stripe_billing_portal_url) });
  } else if (stripe_checkout_session_url) {
    windowModule.navigateToExternal({ url: new URL(stripe_checkout_session_url) });
  } else if (already_paid && isAppleProvider(provider)) {
    // User is already paid via Apple; optimistically reflect Pro
    store.platformInfo.setSubscriptionCompletedSuccessfully();
    await store.platformInfo.fetchPlatformInfo();
  } else {
    logger.error({
      message: "Failed to create checkout session. Unexpected response from endpoint.",
      info: { response: objectModule.safeAsJson(response) },
    });
    throw new BaseError({
      message: "Failed to create checkout session. Unexpected response from endpoint.",
      info: { response: objectModule.safeAsJson(response) },
    });
  }

  return response.data;
}
